import { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import toast, { Toaster } from "react-hot-toast";
import {
  UserCheck, Search, X, Check, Mail, Phone, MapPin, Hash,
  Calendar, IndianRupee, Send
} from "lucide-react";

const STATUS_COLORS = {
  pending:  { bg:"rgba(245,158,11,0.12)", color:"#f59e0b" },
  approved: { bg:"rgba(34,197,94,0.12)",  color:"#22c55e" },
  rejected: { bg:"rgba(239,68,68,0.12)",  color:"#ef4444" },
};

function toDate(val) {
  if (!val) return null;
  if (val?.toDate) return val.toDate();
  return new Date(val);
}

function fmt(d) {
  return d ? d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
}

function isExpired(validUntil) {
  const d = toDate(validUntil);
  if (!d) return false;
  return new Date() > d;
}

export default function RegistrationManager() {
  const [regs,    setRegs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("pending");
  const [search,  setSearch]  = useState("");
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const snap = await getDocs(query(collection(db, "registrations"), orderBy("createdAt", "desc")));
      setRegs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  const filtered = regs.filter(r => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search && !r.fullName?.toLowerCase().includes(search.toLowerCase()) && !r.email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  function openMailClient(reg, decision, reason) {
    const feeLine = `${reg.type?.charAt(0).toUpperCase()}${reg.type?.slice(1)} — ₹${reg.amountPaid || "—"}/year`;
    const subject = decision === "approved"
      ? "Your HTS Trust Application has been Approved!"
      : "Update on your HTS Trust Application";

    const validUntilStr = fmt(toDate(reg.validUntil));

    const body = decision === "approved"
      ? `Dear ${reg.fullName},\n\nCongratulations! Your application to join Help Them Shine Trust as a ${reg.type} (${feeLine}) has been approved.\n\nYour registration is valid until ${validUntilStr} and must be renewed annually.\n\nYou will receive your Welcome Kit, HTS Trust T-Shirt, and ID Card shortly.\n\nWelcome to the team!\n\nHelp Them Shine Trust`
      : `Dear ${reg.fullName},\n\nThank you for your interest in joining Help Them Shine Trust as a ${reg.type}.\n\nAfter review, we are unable to move forward with your application at this time.${reason ? `\n\nReason: ${reason}` : ""}\n\nPlease note the annual registration fee (${feeLine}) is non-refundable as per our terms.\n\nHelp Them Shine Trust`;

    window.location.href = `mailto:${reg.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  async function handleDecision(reg, decision, reason = "") {
    setUpdating(true);
    try {
      const payload = { status: decision, rejectionReason: reason || "" };

      if (decision === "approved") {
        const now = new Date();
        const until = new Date(now);
        until.setFullYear(until.getFullYear() + 1);
        payload.approvedAt = now;
        payload.validFrom  = now;
        payload.validUntil = until;
      }

      await updateDoc(doc(db, "registrations", reg.id), payload);
      setRegs(p => p.map(r => r.id === reg.id ? { ...r, ...payload } : r));
      setSelected(null);
      setShowReject(false);
      setRejectReason("");
      toast.success(`Application ${decision}`);
      openMailClient({ ...reg, ...payload }, decision, reason);
    } catch (e) {
      console.error(e);
      toast.error("Failed to update");
    } finally { setUpdating(false); }
  }

  async function togglePayment(reg) {
    try {
      const newVal = !reg.paymentConfirmed;
      await updateDoc(doc(db, "registrations", reg.id), { paymentConfirmed: newVal });
      setRegs(p => p.map(r => r.id === reg.id ? { ...r, paymentConfirmed: newVal } : r));
      if (selected?.id === reg.id) setSelected(s => ({ ...s, paymentConfirmed: newVal }));
    } catch (e) { toast.error("Failed to update payment status"); }
  }

  const counts = {
    all: regs.length,
    pending: regs.filter(r => r.status === "pending").length,
    approved: regs.filter(r => r.status === "approved").length,
    rejected: regs.filter(r => r.status === "rejected").length,
  };

  return (
    <div>
      <Toaster position="top-center" />

      <div style={s.header}>
        <UserCheck size={22} color="#f59e0b" />
        <div>
          <h2 style={s.title}>Volunteer / Intern / Member Registrations</h2>
          <p style={s.sub}>Review applications, confirm payment, and approve or reject</p>
        </div>
      </div>

      {/* Filters */}
      <div style={s.filterBar}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search size={15} color="#52504d" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." style={s.searchInput} />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["pending", "approved", "rejected", "all"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "7px 14px", borderRadius: 8, border: "1px solid",
              borderColor: filter === f ? "#f59e0b" : "rgba(255,255,255,0.08)",
              background: filter === f ? "rgba(245,158,11,0.12)" : "transparent",
              color: filter === f ? "#f59e0b" : "#78716c",
              fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
            }}>
              {f} ({counts[f]})
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div style={s.empty}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No {filter !== "all" ? filter : ""} applications</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(r => {
            const sc = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
            const expired = r.status === "approved" && isExpired(r.validUntil);
            return (
              <div key={r.id} style={s.row} onClick={() => setSelected(r)}>
                <img src={r.photoUrl} alt={r.fullName} style={s.avatar} onError={e => e.target.style.display = "none"} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fef3e2" }}>{r.fullName}</div>
                  <div style={{ fontSize: 12, color: "#78716c", marginTop: 2, textTransform: "capitalize" }}>
                    {r.type} · ₹{r.amountPaid || "—"}/yr · {r.email} · {r.mobile}
                  </div>
                  {r.status === "approved" && (
                    <div style={{ fontSize: 11, color: expired ? "#ef4444" : "#22c55e", marginTop: 2, fontWeight: 700 }}>
                      {expired ? "⚠ Expired" : "✓ Current"} · Valid until {fmt(toDate(r.validUntil))}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 50, background: r.paymentConfirmed ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.12)", color: r.paymentConfirmed ? "#22c55e" : "#f59e0b" }}>
                  {r.paymentConfirmed ? "Payment ✓" : "Payment Unconfirmed"}
                </span>
                <span style={{ ...s.statusBadge, background: sc.bg, color: sc.color }}>{r.status}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} style={s.closeBtn}><X size={18} /></button>

            <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
              <img src={selected.photoUrl} alt={selected.fullName} style={{ width: 80, height: 80, borderRadius: 12, objectFit: "cover" }} />
              <div>
                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, color: "#fef3e2", margin: "0 0 4px" }}>{selected.fullName}</h3>
                <div style={{ fontSize: 12, color: "#f59e0b", fontWeight: 700, textTransform: "capitalize" }}>{selected.type} Application</div>
                <div style={{ fontSize: 11, color: "#52504d", marginTop: 4, fontFamily: "monospace" }}>ID: {selected.id}</div>
              </div>
            </div>

            {/* Payment */}
            <div style={s.section}>
              <div style={s.sectionTitle}><IndianRupee size={14} color="#f59e0b" /> Payment Status</div>
              <InfoRow icon={IndianRupee} label="Annual Fee" value={`₹${selected.amountPaid || "—"} / Year`} />
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 50, background: selected.paymentConfirmed ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.12)", color: selected.paymentConfirmed ? "#22c55e" : "#f59e0b" }}>
                  {selected.paymentConfirmed ? "Confirmed" : "Unconfirmed — check Razorpay dashboard"}
                </span>
                <button onClick={() => togglePayment(selected)} style={s.smallBtn}>
                  Mark as {selected.paymentConfirmed ? "Unconfirmed" : "Confirmed"}
                </button>
              </div>
            </div>

            {/* Validity */}
            {selected.status === "approved" && (
              <div style={s.section}>
                <div style={s.sectionTitle}><Calendar size={14} color="#f59e0b" /> Registration Validity</div>
                <InfoRow icon={Calendar} label="Approved On" value={fmt(toDate(selected.approvedAt))} />
                <InfoRow icon={Calendar} label="Valid From" value={fmt(toDate(selected.validFrom))} />
                <InfoRow icon={Calendar} label="Valid Until" value={fmt(toDate(selected.validUntil))} />
                <InfoRow icon={Check} label="Renewal Status" value={isExpired(selected.validUntil) ? "Expired — renewal required" : "Current"} />
              </div>
            )}

            {/* Contact */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Contact & Personal Details</div>
              <InfoRow icon={Mail} label="Email" value={selected.email} />
              <InfoRow icon={Phone} label="Mobile" value={selected.mobile} />
              <InfoRow icon={Phone} label="WhatsApp" value={selected.whatsapp} />
              <InfoRow icon={Calendar} label="Date of Birth" value={selected.dob} />
              <InfoRow icon={Hash} label="Gender" value={selected.gender} />
              <InfoRow icon={Hash} label="Blood Group" value={selected.bloodGroup} />
              <InfoRow icon={Hash} label="Father's/Husband's Name" value={selected.guardianName} />
              <InfoRow icon={MapPin} label="Address" value={`${selected.address}, ${selected.city} ${selected.state} ${selected.pincode}`} />
              <InfoRow icon={Hash} label="Aadhaar Number" value={selected.aadharNumber} />
              <InfoRow icon={Hash} label="Reference" value={selected.refName ? `${selected.refName} (${selected.refContact || "—"})` : "—"} />
              <InfoRow icon={Calendar} label="Applied On" value={fmt(toDate(selected.createdAt))} />
            </div>

            {/* Documents */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Documents</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[["Aadhaar Front", selected.aadharFrontUrl], ["Aadhaar Back", selected.aadharBackUrl], ["Photo", selected.photoUrl]].map(([label, url]) => (
                  url && (
                    <a key={label} href={url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                      <img src={url} alt={label} style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)" }} />
                      <div style={{ fontSize: 10, color: "#78716c", textAlign: "center", marginTop: 4 }}>{label}</div>
                    </a>
                  )
                ))}
              </div>
            </div>

            {selected.status === "rejected" && selected.rejectionReason && (
              <div style={{ ...s.section, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
                <div style={{ fontSize: 12, color: "#ef4444", fontWeight: 700 }}>Rejection Reason</div>
                <div style={{ fontSize: 13, color: "#a8a29e", marginTop: 4 }}>{selected.rejectionReason}</div>
              </div>
            )}

            {/* Actions */}
            {selected.status === "pending" && (
              <>
                {showReject ? (
                  <div style={s.section}>
                    <label style={s.sectionTitle}>Rejection Reason (optional)</label>
                    <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                      rows={3} style={{ width: "100%", padding: 10, background: "#0a0908", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 9, color: "#fef3e2", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={() => setShowReject(false)} style={s.ghostBtn}>Cancel</button>
                      <button onClick={() => handleDecision(selected, "rejected", rejectReason)} disabled={updating} style={s.rejectBtn}>
                        <Send size={13} /> Confirm Reject & Open Email
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                    <button onClick={() => setShowReject(true)} style={s.rejectBtn}>
                      <X size={14} /> Reject
                    </button>
                    <button onClick={() => handleDecision(selected, "approved")} disabled={updating} style={s.approveBtn}>
                      <Check size={14} /> Approve & Open Email
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <Icon size={13} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, color: "#52504d", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        <div style={{ fontSize: 13, color: "#fef3e2", marginTop: 2 }}>{value || "—"}</div>
      </div>
    </div>
  );
}

const s = {
  header: { display: "flex", alignItems: "center", gap: 14, marginBottom: 20, background: "#111009", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: 18 },
  title: { fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800, color: "#fef3e2", margin: "0 0 4px" },
  sub: { fontSize: 13, color: "#78716c", margin: 0 },
  filterBar: { display: "flex", gap: 10, flexWrap: "wrap", background: "#111009", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: 14, marginBottom: 16 },
  searchInput: { width: "100%", padding: "9px 12px 9px 36px", background: "#0a0908", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, color: "#fef3e2", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
  row: { display: "flex", alignItems: "center", gap: 14, background: "#111009", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 14, cursor: "pointer" },
  avatar: { width: 44, height: 44, borderRadius: 10, objectFit: "cover", flexShrink: 0 },
  statusBadge: { fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 50, textTransform: "capitalize", flexShrink: 0 },
  empty: { fontSize: 13, color: "#52504d", textAlign: "center", padding: "40px 0" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(10px)" },
  modal: { background: "#111009", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: 28, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto", position: "relative" },
  closeBtn: { position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#a8a29e" },
  section: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 14 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: "#fef3e2", marginBottom: 10, display: "flex", alignItems: "center", gap: 6, textTransform: "uppercase", letterSpacing: "0.5px" },
  smallBtn: { padding: "6px 14px", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", color: "#3b82f6", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  approveBtn: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 12, background: "#22c55e", color: "#0a0908", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  rejectBtn: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 12, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#ef4444", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  ghostBtn: { flex: 1, padding: 12, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#a8a29e", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
};